// Builds links into the HubSpot CRM UI.
//
// Record pages:  https://<host>/contacts/{portalId}/record/{objectTypeId}/{recordId}
// `host` is the portal's region UI domain (e.g. app-na2.hubspot.com), taken from
// the connected integration; falls back to app.hubspot.com (which redirects a
// logged-in user to their region) when it's unknown.
//
// Object type ids verified against live record URLs / HubSpot community docs:
// contact 0-1, company 0-2, deal 0-3, ticket 0-5.
export type HubSpotRecordType = 'contact' | 'company' | 'deal' | 'ticket'

const OBJECT_TYPE_ID: Record<HubSpotRecordType, string> = {
  contact: '0-1',
  company: '0-2',
  deal: '0-3',
  ticket: '0-5',
}

// Accepts "app.hubspot.com" or a region form like "app-na2.hubspot.com" /
// "app-eu1.hubspot.com". Anything else (missing, or the OAuth-time lookup fell
// back to "hubspot.com") → app.hubspot.com.
function hubHost(uiDomain: string | null | undefined): string {
  if (uiDomain && /^app(-[a-z0-9]+)?\.hubspot\.com$/i.test(uiDomain)) return uiDomain
  return 'app.hubspot.com'
}

export function hubspotRecordUrl(
  portalId: number | null | undefined,
  type: HubSpotRecordType,
  recordId: string,
  uiDomain?: string | null,
): string | null {
  if (!portalId) return null
  return `https://${hubHost(uiDomain)}/contacts/${portalId}/record/${OBJECT_TYPE_ID[type]}/${recordId}`
}

// Tasks have no standalone record page in HubSpot — they open in the context of an
// associated record with a `?taskId=` param. Verified against a live task URL:
//   https://app-na2.hubspot.com/contacts/{portalId}/record/0-1/{contactId}?taskId={taskId}
// With no associated contact there's nowhere to anchor it, so fall back to the
// portal's task list.
export function hubspotTaskUrl(
  portalId: number | null | undefined,
  taskId: string,
  associatedContactId: string | null | undefined,
  uiDomain?: string | null,
): string | null {
  if (!portalId) return null
  const host = hubHost(uiDomain)
  if (associatedContactId) {
    return `https://${host}/contacts/${portalId}/record/0-1/${associatedContactId}?taskId=${taskId}`
  }
  return `https://${host}/contacts/${portalId}/objects/0-27/views/all/list`
}
